/**
 * Centralized z-order for the shared map's standard layers (INS-9623).
 *
 * MapLibre has no declarative z-index: `map.addLayer(spec)` with no `beforeId`
 * appends to the TOP of the stack, so toggling a layer off/on (which remounts
 * its react-map-gl `<Layer>`) sends it back to the top and breaks the intended
 * hierarchy. `useEnforceLayerOrder` re-applies the order on every style change.
 *
 * A layer is "managed" iff it carries a `LAYER_ORDER_KEY` band in its `metadata` — which
 * only the standard layer components set (via the `<LayerBand>` wrapper in
 * ordered-layer.tsx). Page-specific / scoped layers never carry it, so they are
 * never reordered. Because ordering is keyed off metadata rather than the layer
 * id, it is immune to id renames and to id-prefix collisions.
 */

// Metadata key each managed layer is tagged with; value is its band (below).
export const LAYER_ORDER_KEY = 'insights:layer-order';

// Bands, bottom -> top. The numeric value IS the z-priority.
export const LAYER_ORDER = {
  landGrid: 1,
  structureGrids: 2,
  tierBasin: 3,
  titleEntityAssets: 4,
  portfolioAssets: 4.5,
  developmentAreas: 5,
  focusAreas: 6,
  pipelines: 7,
  drillingRigs: 8,
  typeCurves: 9,
  wells: 10,
  allocationOverlays: 11,
  allocationTracts: 12,
  unitFill: 12.1,
  unitSetback: 12.2,
  unitLaterals: 12.3,
  // Area Details' own coloured wells: over the shared map's layers, but under a
  // cross-section line the page opens with, which is drawn before they load.
  areaWells: 12.5,
  // The drawn cross-section line sits above everything else the shared map draws:
  // it is what the user is working on, and a well symbol crossing the line they are
  // dragging makes the line look broken.
  crossSection: 13,
} as const;

// Minimal shape we read off `map.getStyle().layers`.
export interface OrderedLayerInfo {
  id: string;
  metadata?: Record<string, unknown> | null;
}

// Within a band, fills sit below lines/outlines, which sit below labels/symbols.
// (Only a tiebreaker among already-managed layers; ids that match none keep
// their existing relative order.)
const renderTypeRank = (id: string): number => {
  if (id.endsWith('-fill') || id.includes('-fill-')) return 0;
  if (
    id.endsWith('-line') ||
    id.endsWith('-lines') ||
    id.endsWith('-outline') ||
    id.includes('-line-')
  ) {
    return 1;
  }
  // labels, pricing, contour grid symbols, etc.
  return 2;
};

// The band a layer belongs to, or null if it is unmanaged (no LAYER_ORDER_KEY metadata).
export const bandOf = (layer: OrderedLayerInfo): number | null => {
  const z = layer.metadata?.[LAYER_ORDER_KEY];
  return typeof z === 'number' ? z : null;
};

// Given the layers in their current stack order (bottom->top, e.g.
// `map.getStyle().layers`), return the managed layer ids in the order they
// SHOULD appear. Unmanaged layers are dropped — they are never moved.
export const computeDesiredOrder = (layers: OrderedLayerInfo[]): string[] =>
  layers
    .map((layer, originalIndex) => ({
      id: layer.id,
      band: bandOf(layer),
      rank: renderTypeRank(layer.id),
      originalIndex,
    }))
    .filter((entry) => entry.band !== null)
    .sort(
      (a, b) =>
        (a.band as number) - (b.band as number) ||
        a.rank - b.rank ||
        a.originalIndex - b.originalIndex
    )
    .map((entry) => entry.id);

// True if the managed layers already appear in `desired` relative order — cheap
// guard to skip moveLayer churn when nothing changed.
export const isAlreadyOrdered = (
  layers: OrderedLayerInfo[],
  desired: string[]
): boolean => {
  const managed = layers.filter((l) => bandOf(l) !== null).map((l) => l.id);
  if (managed.length !== desired.length) return false;
  return managed.every((id, i) => id === desired[i]);
};
